import { html, TemplateResult, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles as componentStyles } from '../view/editorStyles.scss';
import showdown from 'showdown';
import DOMPurify from 'dompurify';
import { translateFn } from '../../../utils/translator.helper';
import { GzDialogWebComponent } from '../../gzDialog';
import {
  GzMarkdownEditorPromptContentWebComponent,
  PromptModes,
  PromptTypes,
} from './GzMarkdownEditorPromptContentWebComponent';
import { GzMarkdownEditorToolbarWebComponent, MDEditorToolbarActions } from './GzMarkdownEditorToolbarWebComponent';
import { getAsteriskCount, getLinkAndImageTypes, openLinksInNewTabExtension } from './helpers';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { classMap } from 'lit/directives/class-map.js';
import { GzMarkdownEditorTextareaWebComponent } from './GzMarkdownEditorTextareaWebComponent';
import { INITIAL_MD_CONTENT, LINK_REGEX, MAX_CHARS } from './constants';
import { GzTooltip } from '../../gzTooltip';
import { copyToClipboard } from '../../../utils/htmlElement.utils';

/**
 * @summary This component is a markdown editor that allows users to write markdown text and see a live preview of the rendered HTML.
 * @tag gz-markdown-editor
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-markdown-editor')
export class GzMarkdownEditorWebComponent extends FormFieldInput<string> {
  static get styles() {
    return [unsafeCSS(`${componentStyles}`)];
  }

  @property({ type: Boolean, attribute: true, reflect: true })
  isError = false;

  @query('.preview')
  private preview!: HTMLElement;

  @query('#textarea')
  private textarea!: GzMarkdownEditorTextareaWebComponent;

  @query('#toolbar')
  private toolbar: GzMarkdownEditorToolbarWebComponent;

  @state()
  private htmlPreview = '';

  @state()
  private charCount = 0;

  @state()
  private textareaSelectedText = '';

  /**
   * Sets the initial value of the textarea, to which the markdown editor will revert to when the reset button is clicked
   */
  @property({ type: String, attribute: false, reflect: true })
  initialContent = INITIAL_MD_CONTENT;

  private converter: showdown.Converter;

  private sanitizationConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'img', 'ol', 'ul', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: ['href', 'src', 'target', 'rel'],
  };

  async firstUpdated() {
    this.converter = new showdown.Converter({
      completeHTMLDocument: true,
      moreStyling: true,
    });
    this.converter.addExtension(openLinksInNewTabExtension, 'openLinksInNewTab'); // Add the extension
    await this.textarea.updateComplete.then(() => {
      this.textarea.setFormValue(this.initialContent);
      this.updatePreview();
    });
  }

  private onTextSizeChange(e: CustomEvent): void {
    this.textarea.applyHeadingFormat(e.detail.value, this.textareaSelectedText);
    this.updatePreview();
  }

  private onToolbarButtonClick(e: CustomEvent): void {
    const { action, event } = e.detail;
    switch (action) {
      case MDEditorToolbarActions.BOLD:
        this.toggleBold();
        break;
      case MDEditorToolbarActions.ITALIC:
        this.toggleItalic();
        break;
      case MDEditorToolbarActions.ORDERED_LIST:
        this.applyListFormatting('ordered');
        break;
      case MDEditorToolbarActions.BULLET_LIST:
        this.applyListFormatting('bullet');
        break;
      case MDEditorToolbarActions.LINK:
        this.toggleLinkPrompt();
        break;
        break;
      case MDEditorToolbarActions.PREVIEW:
        this.openFullPagePreview();
        break;
      case MDEditorToolbarActions.COPY_TO_CLIPBOARD:
        this.copyMarkdownContent(event);
        break;
      case MDEditorToolbarActions.RESET:
        this.resetToDefaults();
        break;
      default:
        break;
    }
  }

  protected renderCounter(): TemplateResult {
    return html`
      <div
        class="${classMap({
          counter: true,
          'max-chars-reached': this.invalid,
        })}"
      >
        <span class="current-count">${this.charCount}</span>
        <span class="separator">/</span>
        <span class="max-count">${MAX_CHARS}</span>
      </div>
    `;
  }

  private renderErrorIcon(): TemplateResult | undefined {
    if (!this.invalid) {
      return;
    }
    return html`<gz-icon
      class="error-icon"
      name="ds-icon-notification_negative_S"
      color="negative"
      size="24"
    ></gz-icon>`;
  }

  render() {
    return html`
      <div class="editor-wrapper">
        <gz-form-field id="textareaField" class="textarea-form-field">
          <div class="editor">
            ${this.renderErrorIcon()}
            <div class="textarea-wrapper">
              <gz-markdown-editor-textarea
                id="textarea"
                gz-form-input
                @change="${this.onTextareaInput}"
                @input="${this.onTextareaInput}"
                @select="${this.onTextareaSelect}"
                @selection-cleared="${this.onTextareaClearSelection}"
              >
              </gz-markdown-editor-textarea>
            </div>
            <gz-md-editor-toolbar
              id="toolbar"
              @text-size-change="${this.onTextSizeChange}"
              @toolbar-button-click="${this.onToolbarButtonClick}"
            ></gz-md-editor-toolbar>
          </div>
          ${this.renderCounter()}
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>

      <div class="preview"></div>
    `;
  }

  private resetToDefaults(): void {
    this.textarea.setMarkdownDefaultContent(this.initialContent);
    this.toolbar.updateResetButtonState(false);
    this.updatePreview();
  }

  private toggleBold(): void {
    this.textarea.toggleFormatting('**');
    this.updatePreview();
    this.updateToolbarState();
  }

  private toggleItalic(): void {
    this.textarea.toggleFormatting('*');
    this.updatePreview();
    this.updateToolbarState();
  }

  private onTextareaClearSelection() {
    this.textareaSelectedText = '';
    this.updateToolbarState();
  }

  private onTextareaSelect(e: CustomEvent): void {
    this.textareaSelectedText = e.detail.text;
    this.updateToolbarState();
  }

  private openFullPagePreview() {
    // needs to be implemented
  }

  private updateToolbarState(): void {
    const selectedText = this.textareaSelectedText;
    const asteriskCount = getAsteriskCount(selectedText);
    if (this.toolbar) {
      this.toolbar.updateFormatButtonsStates(selectedText, asteriskCount);
    }
  }

  private onPromptSubmit(formData: any): void {
    const { url, text } = formData;
    const { selectionStart, selectionEnd } = this.textarea;
    const selectedText = this.textareaSelectedText;
    const { isLink } = getLinkAndImageTypes(selectedText);

    if (!url) {
      // If no URL is provided, just insert the text/remove existing links
      this.textarea.setRangeSelection(text, selectionStart, selectionEnd, 'end');
    } else if (isLink) {
      // Update an existing Markdown link
      const match = selectedText.match(LINK_REGEX);
      if (match) {
        const updatedMarkdown = `[${text || match[1]}](${url})`;
        this.textarea.setRangeSelection(updatedMarkdown, selectionStart, selectionEnd, 'end');
      }
    } else {
      // Insert a new Markdown link
      const format = `[${text || selectedText}](${url})`;
      this.textarea.setRangeSelection(format, selectionStart, selectionEnd, 'end');
    }

    // Update the preview in all cases
    this.updatePreview();
  }

  private toggleLinkPrompt(): void {
    const prompt = new GzMarkdownEditorPromptContentWebComponent();
    prompt.type = PromptTypes.LINK;

    const selectedText = this.textareaSelectedText;
    const { isLink } = getLinkAndImageTypes(selectedText);

    if (isLink) {
      const match = selectedText.match(LINK_REGEX);
      if (match) {
        prompt.url = match[2];
        prompt.text = match[1] || '';
        prompt.mode = PromptModes.EDIT;
      }
    } else {
      prompt.text = selectedText;
      prompt.mode = PromptModes.CREATE;
    }

    const headline = isLink ? translateFn('Edit link') : translateFn('Add link');

    const dialog = GzDialogWebComponent.buildDialogElement({
      headline,
      bodyContent: prompt,
      isOpen: true,
    });

    prompt.addEventListener('submit', (e: CustomEvent) => {
      this.onPromptSubmit(e.detail);
      dialog.close();
      this.updatePreview();
    });
    prompt.addEventListener('cancel', () => dialog.close());
  }

  private onTextareaInput(): void {
    this.dispatchEvent(new CustomEvent('change', { detail: { value: this.getFormValue() } }));
    this.updatePreview();
    if (this.toolbar) {
      this.updateToolbarState();
      this.toolbar.updateResetButtonState(this.textarea.dirty);
    }
  }

  private updatePreview(): void {
    const textareaContent = this.textarea.getFormValue();
    this.charCount = this.textarea.value.length;
    this.htmlPreview = this.converter.makeHtml(textareaContent);
    this.preview.innerHTML = DOMPurify.sanitize(this.htmlPreview, this.sanitizationConfig);
  }

  private getSanitizedMarkdownContent(markdownContent: string): string {
    // Convert markdown to HTML
    const htmlContent = this.converter.makeHtml(markdownContent);
    // Sanitize the HTML using DOMPurify
    const sanitizedHtml = DOMPurify.sanitize(htmlContent, this.sanitizationConfig);
    const content = this.converter.makeMarkdown(sanitizedHtml);
    return content.replace(/<!--\s*-->/g, '').trim();
  }

  private async copyMarkdownContent(e: MouseEvent): Promise<void> {
    const sanitizedMarkdown = this.getSanitizedMarkdownContent(this.textarea?.getFormValue());

    try {
      // attempt to use the new Clipboard API.
      // this will not work if the page is not served over HTTPS, as it happens with the live Storybook preview
      // https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/writeText
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(sanitizedMarkdown);
        // use the old document.execCommand if the new API is not available.
        // this feature is deprecated and will be removed in the future, so we need to consider refactoring it whenever possible
        // https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand
      } else {
        copyToClipboard(sanitizedMarkdown);
      }
      GzTooltip.hide();
      GzTooltip.show(translateFn('Copied to clipboard'), e);
    } catch (error) {
      console.error('Failed to copy to clipboard', error);
    } finally {
      setTimeout(() => {
        GzTooltip.hide();
      }, 2000);
    }
  }

  private applyListFormatting(listType: 'bullet' | 'ordered'): void {
    this.textarea.applyListFormatting(listType);
  }

  getFormValue(): string {
    if (!this.textarea) {
      return '';
    }
    return this.getSanitizedMarkdownContent(this.textarea?.getFormValue());
  }

  setFormValue(value: string): void {
    if (!this.textarea) {
      return;
    }
    this.textarea.setFormValue(value);
    this.updatePreview();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-markdown-editor': GzMarkdownEditorWebComponent;
  }
}
